import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScanLine, CheckCircle2, AlertTriangle } from "lucide-react";

const FIELDS = [
  { k: "insurance_expiry", l: "Insurance" },
  { k: "tax_expiry", l: "Road Tax" },
  { k: "fitness_expiry", l: "Fitness" },
  { k: "puc_expiry", l: "PUC" },
  { k: "permit_expiry", l: "Permit" },
  { k: "national_permit_expiry", l: "Nat. Permit" },
];

export function MparivahanScan({ hub, rows, onDone }) {
  const [scanning, setScanning] = useState(false);
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  const ref = React.useRef(null);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setScanning(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("hub", hub);
      const { data } = await api.post("/compliance/screenshot-ocr", fd, { timeout: 300000, headers: { "Content-Type": "multipart/form-data" } });
      setItems(data.items.map((it) => ({ ...it, apply: !it.error })));
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not read screenshots");
    }
    setScanning(false);
  };

  const setItem = (i, patch) => setItems((p) => p.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const apply = async () => {
    setSaving(true);
    let ok = 0;
    for (const it of items.filter((x) => x.apply && x.vehicle_number)) {
      const cur = rows.find((r) => r.vehicle_number === it.vehicle_number) || {};
      const body = { insurance_provider: it.insurance_provider || cur.insurance_provider || "", notes: cur.notes || "" };
      FIELDS.forEach(({ k }) => { body[k] = it[k] || cur[k] || ""; });
      try {
        await api.put(`/compliance/${it.vehicle_number}`, body, { params: { hub } });
        ok += 1;
      } catch {
        toast.error(`Failed to save ${it.vehicle_number}`);
      }
    }
    setSaving(false);
    toast.success(`Compliance updated for ${ok} vehicle${ok === 1 ? "" : "s"}`);
    setItems(null);
    onDone();
  };

  return (
    <>
      <Button variant="outline" onClick={() => ref.current?.click()} disabled={scanning} data-testid="compliance-mparivahan-scan">
        <ScanLine className="h-4 w-4 mr-1.5" />{scanning ? "Reading screenshots…" : "Scan mParivahan"}
      </Button>
      <input ref={ref} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={onFiles} data-testid="compliance-mparivahan-file" />
      <Dialog open={!!items} onOpenChange={(v) => { if (!v) setItems(null); }}>
        <DialogContent className="max-w-6xl max-h-[88vh] overflow-y-auto" data-testid="mparivahan-review-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanLine className="h-4 w-4 text-primary" />Review mParivahan data</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Check the dates read from each screenshot. Only filled dates overwrite the existing record; empty ones keep the current value.</p>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-xs whitespace-nowrap" data-testid="mparivahan-review-table">
              <thead className="bg-muted/60 text-muted-foreground uppercase">
                <tr>
                  <th className="py-2 px-2 text-left">Apply</th><th className="py-2 px-2 text-left">Screenshot</th><th className="py-2 px-2 text-left">Truck</th>
                  {FIELDS.map((f) => <th key={f.k} className="py-2 px-2 text-left">{f.l}</th>)}
                  <th className="py-2 px-2 text-left">Insurer</th>
                </tr>
              </thead>
              <tbody>
                {(items || []).map((it, i) => (
                  <tr key={i} className="border-t border-border align-top" data-testid={`mparivahan-row-${i}`}>
                    <td className="py-2 px-2"><input type="checkbox" checked={it.apply} disabled={!it.vehicle_number} onChange={(e) => setItem(i, { apply: e.target.checked })} data-testid={`mparivahan-apply-${i}`} /></td>
                    <td className="py-2 px-2 max-w-[140px] truncate" title={it.file}>{it.file}</td>
                    <td className="py-2 px-2">
                      <Input className="h-8 w-32 font-mono text-xs" value={it.vehicle_number || ""} onChange={(e) => setItem(i, { vehicle_number: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })} data-testid={`mparivahan-vehicle-${i}`} />
                      {it.error ? <div className="text-rose-500 text-[10px] mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{it.error}</div>
                        : it.known ? <div className="text-emerald-500 text-[10px] mt-1 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />In fleet</div>
                        : <div className="text-amber-500 text-[10px] mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Not in this hub's fleet</div>}
                    </td>
                    {FIELDS.map((f) => (
                      <td key={f.k} className="py-2 px-2"><Input type="date" className="h-8 w-36 text-xs" value={it[f.k] || ""} onChange={(e) => setItem(i, { [f.k]: e.target.value })} data-testid={`mparivahan-${f.k}-${i}`} /></td>
                    ))}
                    <td className="py-2 px-2"><Input className="h-8 w-36 text-xs" value={it.insurance_provider || ""} onChange={(e) => setItem(i, { insurance_provider: e.target.value })} data-testid={`mparivahan-insurer-${i}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItems(null)} data-testid="mparivahan-cancel">Cancel</Button>
            <Button onClick={apply} disabled={saving || !(items || []).some((x) => x.apply && x.vehicle_number)} data-testid="mparivahan-apply">
              {saving ? "Saving…" : `Apply to ${(items || []).filter((x) => x.apply && x.vehicle_number).length} vehicle(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
